// src/Pages/MDPanelType.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Axios from "axios";
import {
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  NavItem,
  NavSectionHeader,
  NavSubItem,
  Hamburger,
} from "@fluentui/react-nav-preview";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import { toast } from "react-toastify";
import ConfigurationContent from "./ConfigurationContent";
import DevicesContent from "./DevicesContent";
import LogicFunctionContent from "./LogicFunctionContent";
import ZonesContent from "./ZonesContent";
import OutputZonesContent from "./OutputZonesContent";
import MainContent from "./MainContent";
import "../Styles/PanelType.css";
import ConfigFile from "./ParsePanelData";
import defaultPanelData from "../Assets/defaultPanelData/defaultPanelData.json";
import DeleteIcon from "@mui/icons-material/Delete";
import MDUserProfileHeader from "./MDUserProfileHeader";
import panelVersions from "../Config/panelVersions.json"; // version options

class Panel {
  constructor(configFile) {
    this.VT5000_CONFIG = configFile?.VT5000_CONFIG || {
      GeneralParameters: {},
      ArgusTablePr: [],
      Codes: {},
      DaylightSaving: {},
      FlogicPr: [],
      Holidays: [],
      OutputZones: [],
      TimeIntervals: [],
      VTNet: [],
      WeeklyTrends: [],
      ZoneTable: [],
      Dependency: [],
    };
  }
}

const subNavItems = ["Configuration", "Devices", "Logic Function", "Zones", "Output Zones"];

/** Safely parse DB JSON which might arrive as strings */
const parseJSON = (val, fallback) => {
  if (val == null) return fallback;
  if (typeof val !== "string") return val;
  try {
    return JSON.parse(val);
  } catch {
    return fallback;
  }
};

// DB row (projects.*) -> ConfigFile (VT5000_CONFIG) adapter
const parseProjectsRowToConfig = (row) => {
  const cf = new ConfigFile();
  cf.VT5000_CONFIG = {
    ArgusTablePr: parseJSON(row.argustablepr, []),
    Codes: parseJSON(row.codes, {}),
    DaylightSaving: parseJSON(row.daylightsaving, {}),
    FlogicPr: parseJSON(row.flogicpr, []),
    GeneralParameters: parseJSON(row.generalparameters, {}),
    Holidays: parseJSON(row.holidays, []),
    OutputZones: parseJSON(row.outputzones, []),
    TimeIntervals: parseJSON(row.timeintervals, []),
    VTNet: parseJSON(row.vtnets, []),
    WeeklyTrends: parseJSON(row.weeklytrends, []),
    ZoneTable: parseJSON(row.zonetable, []),
    Dependency: parseJSON(row.dependency, []),
  };
  if (!cf.VT5000_CONFIG.GeneralParameters.panelName && row.project_name) {
    cf.VT5000_CONFIG.GeneralParameters.panelName = row.project_name;
  }
  return cf;
};

const MDPanelType = ({ isAdvancedMode }) => {
  // Each item: { project_id, project_name, updated_at?, create_time?, loaded: boolean, panel: Panel|null }
  const [panels, setPanels] = useState([]);
  const [selectedPanelIndex, setSelectedPanelIndex] = useState(null);
  const [selectedSubNavItem, setSelectedSubNavItem] = useState("Main");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [panelName, setPanelName] = useState("");
  const [selectedCheckbox, setSelectedCheckbox] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  // Panel version (lives in this page)
  const defaultVersion = panelVersions?.[0] || "2.0.F7";
  const [panelVersion, setPanelVersion] = useState(
    localStorage.getItem("panelVersion") || defaultVersion
  );
  const userEmail = localStorage.getItem("userEmail") || "";

  const navigate = useNavigate();
  const { mdId } = useParams();
  const siId = localStorage.getItem("siId") || ""; // set at login/selection

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 1) LIST projects (small, fast; no JSON columns) */
  const fetchList = () => {
    Axios.post(`/list_customer_projects`, { mdId, siId })
      .then((res) => {
        const rows = Array.isArray(res?.data?.projects) ? res.data.projects : [];
        const list = rows.map((r) => ({
          project_id: r.project_id,
          project_name: r.project_name || "Unnamed Project",
          updated_at: r.updated_at,
          create_time: r.create_time,
          loaded: false,
          panel: null,
        }));
        setPanels(list);
        if (selectedPanelIndex != null && selectedPanelIndex >= list.length) {
          setSelectedPanelIndex(null);
        }
      })
      .catch((err) => {
        console.error("List projects failed:", err);
        toast.error("Failed to load projects.");
        setPanels([]);
      });
  };

  /** 2) DETAIL project (heavy JSON) for one project_id */
  const loadProjectDetail = async (index) => {
    const item = panels[index];
    if (!item || item.loaded) return;

    try {
      const res = await Axios.post("/get_customer_project", {
        mdId,
        siId,
        projectId: item.project_id,
      });
      if (res?.data?.success && res.data.project) {
        const cf = parseProjectsRowToConfig(res.data.project);
        const wrapped = new Panel(cf);
        setPanels((prev) => {
          const next = [...prev];
          next[index] = { ...next[index], loaded: true, panel: wrapped };
          return next;
        });
      } else {
        toast.error(res?.data?.msg || "Failed to load project.");
      }
    } catch (e) {
      console.error("Load project error:", e);
      toast.error("Error loading project.");
    }
  };

  const updatePanels = async (newProjectId) => {
    // Always refresh list to include newly created project
    try {
      const res = await Axios.post(`/list_customer_projects`, { mdId, siId });
      const rows = Array.isArray(res?.data?.projects) ? res.data.projects : [];
      const list = rows.map((r) => ({
        project_id: r.project_id,
        project_name: r.project_name || "Unnamed Project",
        updated_at: r.updated_at,
        create_time: r.create_time,
        loaded: false,
        panel: null,
      }));

      setPanels(list);

      // If backend gave us the new project_id, auto-select it and load it
      if (newProjectId) {
        const idx = list.findIndex((x) => x.project_id === newProjectId);
        if (idx >= 0) {
          setSelectedPanelIndex(idx);
          setSelectedSubNavItem("Main");
          // load detail for the newly selected project
          // NOTE: use list, not panels, because state updates are async
          const detail = await Axios.post("/get_customer_project", {
            mdId,
            siId,
            projectId: newProjectId,
          });
          if (detail?.data?.success && detail.data.project) {
            const cf = parseProjectsRowToConfig(detail.data.project);
            const wrapped = new Panel(cf);
            setPanels((prev) => {
              const next = [...prev];
              next[idx] = { ...next[idx], loaded: true, panel: wrapped };
              return next;
            });
          }
        }
      }
    } catch (err) {
      console.error("updatePanels refresh failed:", err);
      toast.error("Failed to refresh projects list.");
    }
  };

  const handleCreatePanel = () => {
    if (!projectName.trim()) {
      toast.warning("Please enter a project name.");
      return;
    }

    if (!panelName.trim()) {
      toast.warning("Please enter a panel name.");
      return;
    }

    const newPanelData = JSON.parse(JSON.stringify(defaultPanelData));
    newPanelData.GeneralParameters = newPanelData.GeneralParameters || {};
    newPanelData.GeneralParameters.panelName = panelName.trim();
    newPanelData.GeneralParameters.loopCardNb = selectedCheckbox;

    Axios.post(`/create_customer_project`, {
      email: localStorage.getItem("userEmail") || "",
      panel: newPanelData,
      projectName: projectName.trim(),
    })
      .then((res) => {
        if (res?.data?.success) {
          toast.success("Project created successfully!");
          setIsDialogOpen(false);
          setProjectName("");
          fetchList();
        } else {
          toast.error(res?.data?.msg || "Failed to create project.");
        }
      })
      .catch((err) => {
        console.error("Create project error:", err);
        toast.error("Error creating project.");
      });
  };

  const handleNavItemClick = async (index) => {
    setSelectedPanelIndex(index);
    setSelectedSubNavItem("Main");
    await loadProjectDetail(index);
  };

  const handleSubNavItemClick = (subNavItem) => setSelectedSubNavItem(subNavItem);
  const handleCheckboxChange = (n) => setSelectedCheckbox(n);
  const resetPanelSelection = () => setSelectedPanelIndex(null);

  const handleDeletePanel = () => {
    const item = panels[selectedPanelIndex];
    if (!item) return;
    Axios.post(`/delete_customer_project`, { projectId: item.project_id })
      .then((res) => {
        if (res?.data?.success) {
          toast.success(`Project '${item.project_name}' deleted.`);
          fetchList();
          resetPanelSelection();
        } else {
          toast.error(res?.data?.msg || "Failed to delete project.");
        }
      })
      .catch((err) => {
        console.error("Delete project failed:", err);
        toast.error("Delete endpoint not available.");
      });
    setDeleteDialogOpen(false);
  };

  // Version selector handlers (in this page)
  const handleVersionChange = (e) => {
    const v = e.target.value;
    setPanelVersion(v);
    localStorage.setItem("panelVersion", v);
    window.dispatchEvent(new CustomEvent("panelVersion:changed", { detail: v }));
  };

  const renderContent = () => {
    const selectedItem = panels[selectedPanelIndex];
    if (!selectedItem) return <div>Select a project to see its content.</div>;
    if (!selectedItem.loaded || !selectedItem.panel) return <div>Loading project…</div>;
    // const selected = selectedItem.panel;

    switch (selectedSubNavItem) {
      case "Configuration":
        return (
          <ConfigurationContent
            project={selectedItem}
            panelVersion={panelVersion}
            setSelectedSubNavItem={setSelectedSubNavItem}
            updatePanels={updatePanels}
            isAdvancedMode={isAdvancedMode}
          />
        );
      case "Devices":
        return <DevicesContent project={selectedItem} panelVersion={panelVersion} />;
      case "Logic Function":
        return <LogicFunctionContent project={selectedItem} panelVersion={panelVersion} />;
      case "Zones":
        return <ZonesContent project={selectedItem} panelVersion={panelVersion} />;
      case "Output Zones":
        return <OutputZonesContent project={selectedItem} panelVersion={panelVersion} />;
      default:
        return (
          <MainContent
            project={selectedItem}
            panelVersion={panelVersion}
            updatePanels={updatePanels}
            isAdvancedMode={isAdvancedMode}
          />
        );
    }
  };

  return (
    <div className="dashboard">
      <MDUserProfileHeader isAdmin={true} />
      <div style={{ display: "flex", height: "100vh", flexDirection: "row" }}>
        <NavDrawer
          open
          type="inline"
          style={{
            width: "280px",
            borderRight: "1px solid #ddd",
            boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
            backgroundColor: "#fff",
          }}
        >
          <NavDrawerHeader>
            <Hamburger onClick={() => navigate(`/${mdId}/md_commissioningtool`)} />
          </NavDrawerHeader>

          <NavDrawerBody>
            {/* ===== Version selector ABOVE Projects ===== */}
            <NavSectionHeader>Panel Version</NavSectionHeader>
            <div style={{ padding: "0 12px 12px 12px" }}>
              <select
                value={panelVersion}
                onChange={handleVersionChange}
                style={{
                  width: "100%",
                  height: 36,
                  borderRadius: 8,
                  border: "1px solid #ccc",
                  padding: "0 10px",
                  background: "#fff",
                  cursor: "pointer",
                }}
                aria-label="Select panel version"
              >
                {panelVersions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* ===== Projects list ===== */}
            <NavSectionHeader>Projects</NavSectionHeader>
            <Button onClick={() => setIsDialogOpen(true)}>New Project</Button>

            {panels.map((item, index) => (
              <React.Fragment key={item.project_id || index}>
                <NavItem
                  onClick={() => handleNavItemClick(index)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                      mouseX: e.clientX - 2,
                      mouseY: e.clientY - 4,
                      panelIndex: index,
                    });
                  }}
                  style={{
                    color: selectedPanelIndex === index ? "#c00d0d" : undefined,
                    marginLeft: "10px",
                  }}
                >
                  {(item.project_name || "Unnamed Project")}
                </NavItem>
                {selectedPanelIndex === index && (
                  <div style={{ marginLeft: "20px" }}>
                    {subNavItems.map((subItem) => (
                      <NavSubItem
                        key={subItem}
                        onClick={() => handleSubNavItemClick(subItem)}
                        style={{
                          color: selectedSubNavItem === subItem ? "#c00d0d" : undefined,
                        }}
                      >
                        {subItem}
                      </NavSubItem>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </NavDrawerBody>
        </NavDrawer>

        <div style={{ flex: 1, padding: "16px", color: "black", overflow: "auto" }}>
          {renderContent()}
        </div>

        {/* Create project dialog */}
        <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
          <DialogTitle>CREATE NEW PROJECT</DialogTitle>
          <DialogContent>
            <TextField
              label="Project Name"
              fullWidth
              value={projectName}
              style={{ marginTop: "10px" }}
              onChange={(e) => setProjectName(e.target.value)}
            />
            <TextField
              label="Panel Name"
              fullWidth
              value={panelName}
              style={{ marginTop: "10px" }}
              onChange={(e) => setPanelName(e.target.value)}
            />
            <div style={{ fontSize: "20px", marginBottom: "10px", marginTop: "9%" }}>
              Loop Expansion Cards
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "1px" }}>
              {[0, 1, 2, 3, 4].map((num) => (
                <div
                  key={num}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "60px",
                    height: "60px",
                    border: "2px solid black",
                    borderRadius: "5px",
                    cursor: "pointer",
                    background: selectedCheckbox === num ? "#C00D0D" : "#FFF",
                  }}
                  onClick={() => setSelectedCheckbox(num)}
                >
                  <span
                    style={{
                      fontSize: "30px",
                      fontWeight: "bold",
                      color: selectedCheckbox === num ? "#FFF" : "#000",
                    }}
                  >
                    {num}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "20px", marginTop: "10px" }}>
              Each card is equal to 2 Loop
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePanel} color="primary">
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete project dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Project Deletion</DialogTitle>
          <DialogContent>Are you sure you want to delete this project?</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
              Cancel</Button>
            <Button onClick={handleDeletePanel} color="primary">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Right-click context menu */}
        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                minWidth: 120,
                backgroundColor: "#fff",
                paddingY: 0.5,
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setSelectedPanelIndex(contextMenu.panelIndex);
              setContextMenu(null);
              setDeleteDialogOpen(true);
            }}
            sx={{
              color: "error.main",
              fontWeight: 500,
              gap: 1,
              paddingY: 1,
              "&:hover": { backgroundColor: "#ffe6e6" },
            }}
          >
            <DeleteIcon fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default MDPanelType;
